// Array of EEA countries
import { useHeaderFooter } from '@cdc-internal/mkt-header-footer'

export const EEA_COUNTRIES = [
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'EE', // Estonia
  'FI', // Finland
  'DE', // Germany
  'GR', // Greece
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
  'SE', // Sweden
  'NO', // Norway
  'IS', // Iceland
  'FR', // France
  'IT', // Italy
]

// Array of GCC countries
export const GCC_COUNTRIES = [
  'BH', // Bahrain
  'KW', // Kuwait
  'OM', // Oman
  'QA', // Qatar
  'SA', // Saudi Arabia
]
const ipCountryMap: Record<string, string> = {
  // North America
  US: 'US',
  CA: 'CA',

  // Europe
  FR: 'FR',
  IT: 'IT',
  GB: 'UK',
  TR: 'TR',

  AE: 'AE',
  // EEA Countries
  ...Object.fromEntries(EEA_COUNTRIES.map(country => [country, 'EEA'])),

  // GCC Countries
  ...Object.fromEntries(GCC_COUNTRIES.map(country => [country, 'GCC'])),

  // Others
  SG: 'SG', // Singapore
  BR: 'BR', // Brazil
  AU: 'AU', // Australia
  KR: 'KR', // South Korea
}

// https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
const APAC_AND_MEA_COUNTRIES_ALPHA2 = [
  'AL', // Albania
  'AO', // Angola
  'AI', // Anguilla
  'AG', // Antigua and Barbuda
  'AM', // Armenia
  'AW', // Aruba
  'AU', // Australia
  'AZ', // Azerbaijan
  'BS', // Bahamas
  'BH', // Bahrain
  'BB', // Barbados
  'BZ', // Belize
  'BM', // Bermuda
  'BT', // Bhutan
  'BA', // Bosnia & Herzegovina
  'BR', // Brazil
  'IO', // British Indian Ocean Territory
  'VG', // British Virgin Islands
  'BN', // Brunei
  'KH', // Cambodia
  'CM', // Cameroon
  'CV', // Cape Verde
  'BQ', // Caribbean Netherlands (Bonaire, Sint Eustatius and Saba)
  'KY', // Cayman Islands
  'CL', // Chile
  'CO', // Colombia
  'CR', // Costa Rica
  'CI', // Cote d'Ivoire
  'CW', // Curaçao
  'DO', // Dominican Republic
  'TL', // East Timor (Timor-Leste)
  'EC', // Ecuador
  'EG', // Egypt
  'SV', // El Salvador
  'FK', // Falkland Islands
  'FO', // Faroe Islands
  'FJ', // Fiji
  'PF', // French Polynesia
  'GE', // Georgia
  'GH', // Ghana
  'GL', // Greenland
  'GD', // Grenada
  'GT', // Guatemala
  'HN', // Honduras
  'IN', // India
  'ID', // Indonesia
  'IL', // Israel
  'JM', // Jamaica
  'JP', // Japan
  'JO', // Jordan
  'KZ', // Kazakhstan
  'KE', // Kenya
  'XK', // Kosovo
  'KW', // Kuwait
  'LA', // Laos
  'MG', // Madagascar
  'MY', // Malaysia
  'MV', // Maldives
  'MR', // Mauritania
  'MU', // Mauritius
  'MX', // Mexico
  'MD', // Moldova
  'MN', // Mongolia
  'ME', // Montenegro
  'MA', // Morocco
  'NC', // New Caledonia
  'NI', // Nicaragua
  'NG', // Nigeria
  'NF', // Norfolk Island
  'MK', // North Macedonia
  'OM', // Oman
  'PK', // Pakistan
  'PA', // Panama
  'PG', // Papua New Guinea
  'PY', // Paraguay
  'PE', // Peru
  'PH', // Philippines
  'QA', // Qatar
  'RW', // Rwanda
  'BL', // Saint Barthelemy
  'ST', // Sao Tome & Principe
  'SA', // Saudi Arabia
  'SN', // Senegal
  'RS', // Serbia
  'SC', // Seychelles
  'SG', // Singapore
  'SX', // Sint Maarten
  'SB', // Solomon Islands
  'LK', // Sri Lanka
  'KN', // St. Kitts & Nevis
  'LC', // St. Lucia
  'VC', // St. Vincent and the Grenadines
  'TW', // Taiwan
  'TH', // Thailand
  'TT', // Trinidad & Tobago
  'TN', // Tunisia
  'TM', // Turkmenistan
  'TC', // Turks & Caicos Islands
  'UG', // Uganda
  'AE', // United Arab Emirates
  'UY', // Uruguay
  'UZ', // Uzbekistan
  'VU', // Vanuatu
  'VN', // Vietnam
  'ZM', // Zambia
]

/**
 * keep the same logic from frontend-mkt-content-hub
 */
export const getRegionByCountry = (
  country: string,
): Parameters<typeof useHeaderFooter>[0]['region'] => {
  return (ipCountryMap[country]?.toLowerCase() as any) ?? 'global'
}

const FLAG_IMAGE_PATH_DOMAIN = 'https://mkt-site-asset.crypto.com/assets/country-icon'

export const getCurrencyFlagUrl = (currency: string): string => {
  const currencyIconCodeMap: Record<string, string> = {
    AUD: 'AU',
    BRL: 'BR',
    CAD: 'CA',
    EUR: 'EU',
    GBP: 'UK',
    HKD: 'HK',
    KRW: 'KR',
    RUB: 'RU',
    SGD: 'SG',
    TWD: 'TW',
    USD: 'US',
  }
  const formattedCode = currencyIconCodeMap[currency] || 'GLOBAL'
  return `${FLAG_IMAGE_PATH_DOMAIN}/flag-${formattedCode.toLowerCase()}.webp`
}

/**
 * TODO: for Country Code KR,we need force redirect it's path to kr, if the path is not kr or en-kr.
 * After implement it, could remove the params countryCode
 */
/**
 * Check if the given locale represents a Korean region
 * @param locale - The locale string to check
 * @param countryCode - The code from X-Forwarded-Country
 * @returns true if the locale is 'kr' or 'en-kr', false otherwise
 */
export const isKoreanRegion = (locale: string, countryCode: string): boolean => {
  return locale === 'kr' || locale === 'en-kr' || countryCode === 'KR'
}

export const isEEARegion = (locale: string, countryCode: string): boolean => {
  return locale === 'eea' || EEA_COUNTRIES.includes(countryCode)
}

/**
 * Check if user is in a restricted region based on region restrictions from API
 * @param regionRestrictions - Array of restricted region codes from API (e.g., ['EEA', 'UK', 'US'])
 * @param countryCode - The user's country code
 * @param locale - The user's locale
 * @returns true if user is in a restricted region
 */
export const isUserInRestrictedRegion = (
  regionRestrictions: string[] | undefined,
  countryCode: string,
  locale: string,
): boolean => {
  if (!regionRestrictions || regionRestrictions.length === 0) {
    return false
  }

  for (const restriction of regionRestrictions) {
    const upperRestriction = restriction.toUpperCase()
    if (upperRestriction === 'EEA' && isEEARegion(locale, countryCode)) {
      return true
    }
  }

  return false
}

export function isAPACAndMEACountry(countryCode: string): boolean {
  return !!countryCode && APAC_AND_MEA_COUNTRIES_ALPHA2.includes(countryCode)
}

export function isCAUser(countryCode: string): boolean {
  return countryCode === 'CA'
}

export function isUSUser(countryCode: string): boolean {
  return countryCode === 'US'
}

export function isUSRegion(locale: string): boolean {
  return locale === 'us' || locale === 'es-us'
}
